import { faCommentsQuestion } from "@jewlr/pro-regular-svg-icons/faCommentsQuestion"
import { faGem } from "@jewlr/pro-regular-svg-icons/faGem"
import { faGift } from "@jewlr/pro-regular-svg-icons/faGift"
import { faGlobe } from "@jewlr/pro-regular-svg-icons/faGlobe"
import { faQuestionCircle } from "@jewlr/pro-regular-svg-icons/faQuestionCircle"
import { Box, Button, Flex, Typography } from "@jewlr/storybook/core"
import PropTypes from "prop-types"
import { Link } from "react-router-dom"
import styled from "styled-components"

import { TitleWithDivider } from "areas/helpdesk/index"
import Loading from "areas/helpdesk/loading"
import FontAwesomeIcon from "components/ui/font-awesome-icon"
import fetchCollectionData from "hoc/helpdesk/fetch-collection-results"
import withHelpdesk from "hoc/helpdesk/with-helpdesk"

const HomepageCategory = styled(Typography).attrs((props) => ({
  as: "h3",
  fontSize: { _: props.theme.fontSizes[18], tablet: props.theme.fontSizes[24] },
  ...props.theme.helpdesk.homepageCategory,
}))`
  ${({ theme }) => `
    color: ${theme.helpdesk.box.text};
    font-weight: normal;
    padding: ${theme.space[3]} 0;
    ${theme.mediaQueries.tablet`
    `}
  `}
`

const CategoryLink = styled(Box).attrs((props) => ({
  color: props.theme.helpdesk.box.text,
}))`
  border-bottom: 1px solid;
  ${({ theme }) => `
    display: inline-block;
    width: 100%;
    :first-child{
      border-top: ${
        theme.helpdesk.categoryLink
          ? `1px solid ${theme.colors.grey[10]}`
          : "none"
      };
    }
    :last-child {
      border-bottom: ${
        theme.helpdesk.categoryLink
          ? theme.helpdesk.categoryLink.borderBottom
          : "none"
      };
    }
  `}
  ${(props) => props.theme.helpdesk.categoryLink}
`
const InlineLink = styled(Typography).attrs(() => ({
  as: "a",
  fontSize: 16,
  href: `mailto:${sl.config.customer_service_email}`,
}))`
  ${({ theme }) => `
    color: ${theme.helpdesk.inlineLink.color};
    text-decoration: ${theme.helpdesk.inlineLink.textDecoration};
  `}
`

const ContactFlex = styled(Flex).attrs((props) => ({
  px: { _: 3, tablet: 6 },
  py: props.theme.helpdesk.contact.py,
  width: { _: "100%", tablet: "80%" },
}))`
  ${({ theme }) => `
    align-items: center;
    background-color: ${theme.helpdesk.box.background};
    flex-direction: column;
    height: 100%;
    justify-content: center;
  `}
`
const ContactButton = styled(Button).attrs((props) => ({
  ...props.theme.helpdesk.contactButton.attrs,
  ...props,
}))`
  ${(props) => props.theme.helpdesk.contactButton.style}
`

const ContactTitle = styled(Typography).attrs((props) => ({
  color: "black",
  fontSize: 24,
  px: 5,
  ...props.theme.helpdesk.contactTitle,
}))``

const ContactDescription = styled(Typography).attrs((props) => ({
  fontSize: 16,
  mx: 3,
  my: 3,
  ...props.theme.helpdesk.contactDescription,
}))``

const BrowseHelpTopics = ({ results }) => {
  if (!results) {
    return <Loading />
  }
  const collectionIds = Object.keys(results)
  if (!collectionIds.length) {
    return <Loading />
  }
  const categoryIdPairs = [
    // Order Information / Returns
    {
      categoryId: "5ea1ec702c7d3a7e9aeb86e1",
      collectionId: "5e9f516d2c7d3a7e9aeb5bfe",
    },
    // Order Information / Repairs and Resizing
    {
      categoryId: "5ea1ec692c7d3a7e9aeb86e0",
      collectionId: "5e9f516d2c7d3a7e9aeb5bfe",
    },
    // Order Information / Shipping
    {
      categoryId: "5ea1ec7b04286364bc98e7ba",
      collectionId: "5e9f516d2c7d3a7e9aeb5bfe",
    },
    // General Questions / VIP Rewards
    {
      categoryId: "5ea1a14c2c7d3a7e9aeb8024",
      collectionId: "5e9f51622c7d3a7e9aeb5bfc",
    },
  ]

  const availableAssetSlugs = {
    "general-questions": faCommentsQuestion,
    "international-orders": faGlobe,
    "jewelry-education": faGem,
    "order-information": faGift,
  }

  return (
    <>
      <div className="tablet:gap-24 tablet:grid-cols-2 grid grid-cols-1 gap-8 px-24">
        {collectionIds.length > 0 &&
          collectionIds.map((collectionId) => {
            const collection = results[collectionId]
            const icon = availableAssetSlugs[collection.slug]

            if (
              !collection ||
              !Object.keys(availableAssetSlugs).includes(collection.slug)
            ) {
              return null
            }

            return (
              <Link
                className="tablet:min-h-250 block"
                data-lc="homepage"
                key={`collection-${collectionId}`}
                to={`/helpdesk/${collectionId}`}
              >
                <div className="tablet:px-40 tablet:py-80 bg-grey-10 tablet:flex-col tablet:gap-32 flex h-full flex-row items-center justify-center gap-8 px-16 py-32 text-center">
                  <FontAwesomeIcon
                    className="text-32 tablet:text-48"
                    data-cy={collection.name}
                    icon={icon}
                  />
                  <h2 className="text-16 tablet:text-24">{collection.name}</h2>
                </div>
              </Link>
            )
          })}
      </div>
      <Box margin="auto" py={3} width={{ _: "80%", tablet: "50%" }}>
        <TitleWithDivider text="More Options" />
      </Box>
      <Flex flexDirection={{ _: "column", tablet: "inherit" }}>
        <Box flex="1" order={{ _: 2, tablet: 1 }} textAlign="center">
          <ContactFlex>
            <ContactTitle>We&apos;re Here To Help!</ContactTitle>
            <ContactDescription>
              Please send us an <InlineLink>email</InlineLink> with a brief
              summary of your concern. Our agents will be happy to assist. We
              are also available via LiveChat from Monday-Friday, 9:00am -
              5:30pm ET.
            </ContactDescription>
            <ContactButton
              data-lc="homepage"
              forwardedAs={Link}
              to="/helpdesk/contact"
            >
              Contact Us
            </ContactButton>
          </ContactFlex>
        </Box>
        <Box
          flex="1"
          mb={{ _: 5, tablet: 0 }}
          mx={{ _: 3, tablet: 0 }}
          order={{ _: 1, tablet: 2 }}
          px={1}
        >
          {categoryIdPairs.map((idPair) => {
            const collection = results[idPair.collectionId]
            if (!collection) {
              return ""
            }
            const category =
              collection && collection.categories[idPair.categoryId]
            if (!category) {
              return ""
            }

            return (
              <CategoryLink
                as={Link}
                data-lc="homepage"
                key={`collection-category-${idPair.categoryId}`}
                to={`/helpdesk/${idPair.collectionId}/${idPair.categoryId}`}
              >
                <HomepageCategory>
                  <FontAwesomeIcon
                    className="relative mr-16"
                    icon={faQuestionCircle}
                  />
                  {category.name}
                </HomepageCategory>
              </CategoryLink>
            )
          })}
        </Box>
      </Flex>
    </>
  )
}

BrowseHelpTopics.propTypes = {
  results: PropTypes.object,
}

export default fetchCollectionData(withHelpdesk(BrowseHelpTopics))
